import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { FiArrowLeft, FiCalendar } from 'react-icons/fi';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, Cell 
} from 'recharts';
import Logo from '../components/Logo';
import styles from './AnalyticsDashboard.module.css';

export default function AnalyticsDashboard() {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('week'); // week, month, year
  const [metrics, setMetrics] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    avgOrderValue: 0,
    topItems: [],
    ordersByDay: [],
    revenueByDay: [],
    ordersByStatus: [],
    hourlyDistribution: []
  });

  useEffect(() => {
    fetchRestaurantData();
    fetchAnalyticsData();
  }, [restaurantId, dateRange]);

  const fetchRestaurantData = async () => {
    try {
      const { data, error } = await supabase
        .from('restaurants')
        .select('*')
        .eq('id', restaurantId)
        .single();
        
      if (error) throw error;
      setRestaurant(data);
    } catch (error) {
      console.error('Error fetching restaurant:', error);
    }
  };

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      // Set date range for query
      const now = new Date();
      let startDate;
      
      switch (dateRange) {
        case 'week':
          startDate = new Date(now);
          startDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          startDate = new Date(now);
          startDate.setMonth(now.getMonth() - 1);
          break;
        case 'year':
          startDate = new Date(now);
          startDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          startDate = new Date(now);
          startDate.setDate(now.getDate() - 7);
      }
      
      const startDateStr = startDate.toISOString();
      
      // Fetch total orders and revenue
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select(`
          id,
          total_amount,
          status,
          created_at,
          order_items (
            id,
            menu_item_id,
            menu_item_name,
            price,
            quantity
          )
        `)
        .eq('restaurant_id', restaurantId)
        .gte('created_at', startDateStr)
        .order('created_at', { ascending: false });
        
      if (ordersError) throw ordersError;
      
      // Calculate basic metrics
      const totalOrders = ordersData.length;
      const totalRevenue = ordersData.reduce((sum, order) => sum + order.total_amount, 0);
      const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
      
      // Process orders by day
      const ordersByDayMap = {};
      const revenueByDayMap = {};
      
      // Process orders by status
      const statusCounts = {
        'new': 0,
        'preparing': 0,
        'ready': 0,
        'completed': 0,
        'cancelled': 0
      };
      
      // Process hourly distribution
      const hourlyDistribution = Array(24).fill(0);
      
      // Process top items
      const itemsMap = {};
      
      // Populate data structures
      ordersData.forEach(order => {
        // Process by day
        const date = new Date(order.created_at).toLocaleDateString();
        ordersByDayMap[date] = (ordersByDayMap[date] || 0) + 1;
        revenueByDayMap[date] = (revenueByDayMap[date] || 0) + order.total_amount;
        
        // Process by status
        if (statusCounts[order.status] !== undefined) {
          statusCounts[order.status]++;
        }
        
        // Process by hour
        const hour = new Date(order.created_at).getHours();
        hourlyDistribution[hour]++;
        
        // Process items
        order.order_items.forEach(item => {
          const itemId = item.menu_item_id;
          if (!itemsMap[itemId]) {
            itemsMap[itemId] = {
              id: itemId,
              name: item.menu_item_name,
              quantity: 0,
              revenue: 0
            };
          }
          itemsMap[itemId].quantity += item.quantity;
          itemsMap[itemId].revenue += item.price * item.quantity;
        });
      });
      
      // Format data for charts
      const ordersByDay = Object.entries(ordersByDayMap).map(([date, count]) => ({
        date,
        orders: count
      })).sort((a, b) => new Date(a.date) - new Date(b.date));
      
      const revenueByDay = Object.entries(revenueByDayMap).map(([date, amount]) => ({
        date,
        revenue: parseFloat(amount.toFixed(2))
      })).sort((a, b) => new Date(a.date) - new Date(b.date));
      
      const ordersByStatus = Object.entries(statusCounts).map(([status, count]) => ({
        status,
        count
      }));
      
      const hourlyData = hourlyDistribution.map((count, hour) => ({
        hour: `${hour}:00`,
        orders: count
      }));
      
      // Get top items
      const topItems = Object.values(itemsMap)
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10);
      
      setMetrics({
        totalOrders,
        totalRevenue,
        avgOrderValue,
        topItems,
        ordersByDay,
        revenueByDay,
        ordersByStatus,
        hourlyDistribution: hourlyData
      });
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];
  const STATUS_COLORS = {
    'new': '#3498db',
    'preparing': '#f39c12',
    'ready': '#2ecc71',
    'completed': '#27ae60',
    'cancelled': '#e74c3c'
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.pageHeader}>
          <h1>{restaurant?.name || 'Analytics'}</h1>
          <button 
            onClick={() => navigate(`/dashboard`)}
            className={styles.backButton}
          >
            <FiArrowLeft size={16} /> Back to Dashboard
          </button>
        </div>
        
        {/* Add the date range selector here */}
        <div className={styles.filterSection}>
          <div className={styles.dateRangeSelector}>
            <FiCalendar size={16} />
            <select 
              value={dateRange} 
              onChange={(e) => setDateRange(e.target.value)}
              className={styles.dateRangeSelect}
            >
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="year">Last Year</option>
            </select>
          </div>
        </div>
        
        {loading ? (
          <div className={styles.loadingState}>
            <div className={styles.loadingSpinner}></div>
            <p>Loading analytics data...</p>
          </div>
        ) : (
          <>
            {/* Key metrics */}
            <div className={styles.metricsGrid}>
              <div className={styles.metricCard}>
                <h3 className={styles.metricTitle}>Total Orders</h3>
                <div className={styles.metricValue}>{metrics.totalOrders}</div>
              </div>

              <div className={styles.metricCard}>
                <h3 className={styles.metricTitle}>Total Revenue</h3>
                <div className={styles.metricValue}>${metrics.totalRevenue.toFixed(2)}</div>
              </div>

              <div className={styles.metricCard}>
                <h3 className={styles.metricTitle}>Average Order Value</h3>
                <div className={styles.metricValue}>${metrics.avgOrderValue.toFixed(2)}</div>
              </div>
            </div>

            {/* Charts */}
            <div className={styles.chartGrid}>
              {/* Orders by day */}
              <div className={styles.chartCard}>
                <h3 className={styles.chartTitle}>Orders by Day</h3>
                <div className={styles.chartContainer}>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart 
                      data={metrics.ordersByDay} 
                      margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="orders" 
                        stroke="#635BFF" 
                        strokeWidth={2}
                        activeDot={{ r: 8 }} 
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Revenue by day */}
              <div className={styles.chartCard}>
                <h3 className={styles.chartTitle}>Revenue by Day</h3>
                <div className={styles.chartContainer}>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart 
                      data={metrics.revenueByDay}
                      margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip formatter={(value) => [`$${value}`, 'Revenue']} />
                      <Legend />
                      <Bar dataKey="revenue" fill="#48BB78" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Orders by status */}
              <div className={styles.chartCard}>
                <h3 className={styles.chartTitle}>Orders by Status</h3>
                <div className={styles.chartContainer}>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={metrics.ordersByStatus}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                        nameKey="status"
                      >
                        {metrics.ordersByStatus.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={STATUS_COLORS[entry.status] || COLORS[index % COLORS.length]} 
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Hourly order distribution */}
              <div className={styles.chartCard}>
                <h3 className={styles.chartTitle}>Orders by Hour</h3>
                <div className={styles.chartContainer}>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                      data={metrics.hourlyDistribution}
                      margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="orders" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Top selling items */}
            <div className={styles.tableCard}>
              <h3 className={styles.chartTitle}>Top Selling Items</h3>
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Quantity Sold</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.topItems.map(item => (
                    <tr key={item.id}>
                      <td>{item.name}</td>
                      <td>{item.quantity}</td>
                      <td>${item.revenue.toFixed(2)}</td>
                    </tr>
                  ))}
                  {metrics.topItems.length === 0 && (
                    <tr>
                      <td colSpan="3" className={styles.noDataMessage}>No items sold in this period</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}